import { useState } from "react";
import { Plus } from "lucide-react";
import {
  Page,
  Table,
  type Column,
  Button,
  Text,
  Badge,
  EmptyState,
} from "@marekl/adminer/components";
import { useConfirm } from "@marekl/adminer/hooks";
import { notifyError, notifySuccess } from "@marekl/adminer/api";
import { useIsAdmin } from "../../auth/useAuth";
import {
  useDeleteSender,
  useSenderList,
  useTestSender,
  type Sender,
} from "../../lib/queries/senders";
import { SenderFormModal } from "./SenderModals";
import { RefreshButton } from "../../components/RefreshButton";

type ModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; sender: Sender };

const TLS_LABELS: Record<string, string> = {
  implicit: "TLS",
  starttls: "STARTTLS",
  none: "no encryption",
};

/** Everything this page shows, for the header's refresh button. */
const REFRESH_KEYS = [["senders"]];

export function SendersPage() {
  const isAdmin = useIsAdmin();
  const { data, isLoading, error, refetch } = useSenderList();
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [testingId, setTestingId] = useState<string | null>(null);
  const testSender = useTestSender();
  const del = useDeleteSender();
  const confirm = useConfirm();
  const close = () => setModal({ kind: "closed" });

  function confirmDelete(sender: Sender) {
    void confirm({
      title: "Delete sender",
      description: (
        <>
          Really delete the sender <strong>{sender.name}</strong> (
          <code>{sender.key}</code>)?
        </>
      ),
      confirmLabel: "Delete",
      variant: "danger",
      // A 409 (sender still has messages) rejects and keeps the dialog open
      // with the server's message; useDeleteSender suppresses the toast.
      onConfirm: async () => {
        await del.mutateAsync({ id: sender.id });
      },
    }).then((confirmed) => {
      if (confirmed) notifySuccess("The sender was deleted.");
    });
  }

  async function runTest(sender: Sender) {
    setTestingId(sender.id);
    try {
      await testSender.mutateAsync({ id: sender.id });
      notifySuccess(`Connection to ${sender.smtp?.host ?? "the server"} works.`);
    } catch (err) {
      notifyError(err);
    } finally {
      setTestingId(null);
    }
  }

  const columns: Column<Sender>[] = [
    {
      key: "name",
      header: "Name",
      primary: true,
      render: (row) => row.name,
    },
    {
      key: "key",
      header: "Key",
      render: (row) => <Text variant="mono">{row.key}</Text>,
    },
    {
      key: "from",
      header: "Sends as",
      render: (row) =>
        row.from_name ? `${row.from_name} <${row.from_address}>` : row.from_address,
    },
    {
      key: "smtp",
      header: "Delivery",
      render: (row) =>
        row.type === "dummy" ? (
          <Badge weight="ghost" variant="warn">Dummy (never delivers)</Badge>
        ) : row.smtp ? (
          <Text variant="mono">
            {row.smtp.host}
            {row.smtp.port ? `:${row.smtp.port}` : ""}
            {row.smtp.tls_mode ? ` (${TLS_LABELS[row.smtp.tls_mode] ?? row.smtp.tls_mode})` : ""}
          </Text>
        ) : (
          <Text variant="muted">—</Text>
        ),
    },
    {
      key: "limits",
      header: "Limits",
      render: (row) =>
        row.type === "dummy" ? (
          <Text variant="muted">—</Text>
        ) : (
        <>
        <Text variant="mono">
          {row.max_in_flight}×
          {row.rate_per_minute != null ? ` · ${row.rate_per_minute}/min` : ""}
        </Text>
        {/* Visible here so a 422 from a fenced sender is not a puzzle. */}
        {row.smtp?.allowed_recipients?.length ? (
          <>
            {" "}
            <Badge weight="ghost" variant="info" title={row.smtp.allowed_recipients.join("\n")}>
              Only {row.smtp.allowed_recipients.length === 1
                ? row.smtp.allowed_recipients[0]
                : `${row.smtp.allowed_recipients.length} allowed entries`}
            </Badge>
          </>
        ) : null}
        </>
      ),
    },
    {
      key: "active",
      header: "Status",
      render: (row) =>
        row.active ? (
          <Badge weight="ghost" variant="ok" dot>Active</Badge>
        ) : (
          <Badge weight="ghost" variant="muted" dot>Inactive</Badge>
        ),
    },
    ...(isAdmin
      ? [
          {
            key: "actions",
            header: "",
            render: (row: Sender) => (
              <Table.Actions>
                {/* Only an SMTP sender has a connection to test: an SMS or a
                    dummy sender has no SMTP account, and the server's test
                    opens one. The action would always fail. */}
                {row.type === "smtp" && (
                  <Table.Action
                    onClick={() => runTest(row)}
                    disabled={testingId === row.id}
                  >
                    {testingId === row.id ? "Testing…" : "Test connection"}
                  </Table.Action>
                )}
                <Table.Action onClick={() => setModal({ kind: "edit", sender: row })}>
                  Edit
                </Table.Action>
                <Table.Action variant="danger" onClick={() => confirmDelete(row)}>
                  Delete
                </Table.Action>
              </Table.Actions>
            ),
          } satisfies Column<Sender>,
        ]
      : []),
  ];

  return (
    <>
      <Page>
        <Page.Header
          title="Senders"
          subtitle="the SMTP accounts mail is delivered from"
          subtitleVariant="tagline"
          actions={
            <>
              <RefreshButton queryKeys={REFRESH_KEYS} />
              {isAdmin && (
                <Button variant="primary" onClick={() => setModal({ kind: "create" })}>
                  <Plus size={16} aria-hidden="true" />
                  Add sender
                </Button>
              )}
            </>
          }
        />
        <Page.Content stack gap="lg">
          <Table<Sender>
            striped
            cards
            columns={columns}
            rows={data ?? []}
            rowKey={(r) => r.id}
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
            empty={
              <EmptyState
                title="No senders"
                description="Add the first sender so there is somewhere to deliver from."
              />
            }
          />
        </Page.Content>
      </Page>

      {modal.kind === "create" && <SenderFormModal onClose={close} />}
      {modal.kind === "edit" && (
        <SenderFormModal initial={modal.sender} onClose={close} />
      )}
    </>
  );
}
